import type { FieldInsightItem, SiblingDataSet } from "./dataSetApi";

// Pure transforms behind the compareSchemaDataSets tool. Kept out of the React
// component so they can be unit-tested without a DOM.

export interface SiblingProfilingSummary {
    /** Number of fields that carry profiling stats for this data set. */
    profiledFieldsCount: number;
    /** Mean completeness across profiled fields, 0-100, rounded to 1dp. */
    avgCompletePct?: number;
    /** Mean uniqueness across profiled fields, 0-100, rounded to 1dp. */
    avgUniquenessPct?: number;
    /** Fields with the lowest completeness, worst first (max 3). */
    lowestCompletenessFields?: Array<{ fieldName: string; completePct: number }>;
    /** True when the data set has never been profiled. */
    noProfilingData?: boolean;
}

export interface SiblingSummary {
    dataSetId: string;
    dataSetName: string;
    isCurrent: boolean;
    recordCount?: number;
    erroredRecordsCount?: number;
    /** erroredRecordsCount / recordCount as a percentage, 1dp. Omitted when recordCount is 0/unknown. */
    errorRatePct?: number;
    rating?: number;
    tags?: Record<string, string[]>;
    updatedDate?: string;
    createdDate?: string;
    profiling?: SiblingProfilingSummary;
}

export function round1(n: number): number {
    return Math.round(n * 10) / 10;
}

export function summariseInsights(insights: FieldInsightItem[]): SiblingProfilingSummary {
    const profiled = (insights || []).filter((i) => i?.profilingStats && i.profilingStats.skipped !== true);
    if (profiled.length === 0) return { profiledFieldsCount: 0, noProfilingData: true };

    const completes: number[] = [];
    const uniques: number[] = [];
    const perField: Array<{ fieldName: string; completePct: number }> = [];
    for (const i of profiled) {
        const c = i.profilingStats?.completePct;
        const u = i.profilingStats?.uniquenessPct;
        if (typeof c === "number") {
            completes.push(c);
            perField.push({ fieldName: i.fieldName || i.fieldId || "(unnamed field)", completePct: round1(c) });
        }
        if (typeof u === "number") uniques.push(u);
    }

    const summary: SiblingProfilingSummary = { profiledFieldsCount: profiled.length };
    if (completes.length > 0) summary.avgCompletePct = round1(completes.reduce((a, b) => a + b, 0) / completes.length);
    if (uniques.length > 0) summary.avgUniquenessPct = round1(uniques.reduce((a, b) => a + b, 0) / uniques.length);
    if (perField.length > 0) {
        summary.lowestCompletenessFields = [...perField].sort((a, b) => a.completePct - b.completePct).slice(0, 3);
    }
    return summary;
}

// Map the raw sibling data sets onto the compact comparison rows the LLM reads.
// Sorted largest-first so the model leads with the meaningful comparison; the
// current data set is always present and flagged via isCurrent.
export function buildSiblingSummaries(dataSets: SiblingDataSet[], currentDataSetId?: string): SiblingSummary[] {
    const rows: SiblingSummary[] = (dataSets || []).map((ds) => {
        const recordCount = typeof ds.recordCount === "number" ? ds.recordCount : undefined;
        const erroredRecordsCount = typeof ds.erroredRecordsCount === "number" ? ds.erroredRecordsCount : undefined;
        const summary: SiblingSummary = {
            dataSetId: ds.dataSetId,
            dataSetName: ds.dataSetName || ds.dataSetId,
            isCurrent: !!currentDataSetId && ds.dataSetId === currentDataSetId,
        };
        if (recordCount !== undefined) summary.recordCount = recordCount;
        if (erroredRecordsCount !== undefined) summary.erroredRecordsCount = erroredRecordsCount;
        if (recordCount !== undefined && recordCount > 0 && erroredRecordsCount !== undefined) {
            summary.errorRatePct = round1((erroredRecordsCount / recordCount) * 100);
        }
        if (ds.metadata?.rating !== undefined) summary.rating = ds.metadata.rating;
        if (ds.metadata?.tags && Object.keys(ds.metadata.tags).length > 0) summary.tags = ds.metadata.tags;
        if (ds.updatedDate) summary.updatedDate = ds.updatedDate as string;
        if (ds.createdDate) summary.createdDate = ds.createdDate as string;
        return summary;
    });

    rows.sort((a, b) => (b.recordCount ?? -1) - (a.recordCount ?? -1));
    return rows;
}

// PRE-2672 guard: profiling is one extra request PER data set, so the fan-out is
// bounded. The record-count / error-count comparison always covers every sibling
// (it rides on the single list call); only the per-field profiling pass is capped.
export const MAX_PROFILING_DATASETS = 6;

// Current data set first, then the largest siblings, capped. Keeps the extra
// requests bounded so a schema with many data sets cannot blow the chat timeout.
export function selectProfilingTargets(rows: SiblingSummary[]): SiblingSummary[] {
    return [...rows].sort((a, b) => Number(b.isCurrent) - Number(a.isCurrent)).slice(0, MAX_PROFILING_DATASETS);
}
